import { useState, useEffect, useRef } from "react";
import { Link2, Copy, Check, ScanLine, X } from "lucide-react";
import GlassCard from "./ui/GlassCard";
import { QRCodeSVG } from "qrcode.react";

/*
  QRShareCard
  ------------------------------------------------------------------
  Matches the app's existing glass-card language (GlassCard,
  UsageLimits, ProgressBar): frosted surface, hairline borders,
  Space Grotesk headings, slate-900/white text, #8b7cf6 (violet) /
  #34d9b8 (teal) / #FF5C7A (pink) accent system.

  Swap `renderQr` for your real QR source (e.g. qrcode.react's
  <QRCodeSVG value={link} .../> or react-qr-code) — the pattern below
  is a deterministic placeholder so this file has zero extra deps.
*/

const ACCENT_VIOLET = "#8b7cf6";
const ACCENT_TEAL = "#34d9b8";
const ACCENT_PINK = "#FF5C7A";



// ---------------------------------------------------------------------------
// Toast — same visual DNA as the "Limit reached" / "Free" badges in
// UsageLimits (rounded pill, tinted background, tiny bold accent), scaled
// up into a proper top-center notification with an icon chip and gradient
// edge so it reads as premium rather than a generic browser alert.
// ---------------------------------------------------------------------------

function CopyToast({ show, onClose }) {
  const timerRef = useRef(null);

  useEffect(() => {
    if (show) {
      timerRef.current = setTimeout(onClose, 2400);
    }
    return () => clearTimeout(timerRef.current);
  }, [show, onClose]);

  return (
    <div
      className={`fixed left-1/2 top-6 z-[100] -translate-x-1/2 transition-all duration-300 ease-out ${
        show
          ? "opacity-100 translate-y-0 scale-100"
          : "pointer-events-none opacity-0 -translate-y-2 scale-95"
      }`}
    >
      <div className="relative flex items-center gap-3 rounded-2xl border border-black/[0.06] dark:border-white/[0.08] bg-white/90 dark:bg-[#13152c]/95 backdrop-blur-xl shadow-[0_16px_40px_-12px_rgba(15,23,42,0.25)] pl-2 pr-4 py-2 overflow-hidden">
        <span
          className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full ml-1"
          style={{ backgroundColor: `${ACCENT_TEAL}1f`, color: ACCENT_TEAL }}
        >
          <Check size={16} strokeWidth={3} />
        </span>
        <div className="flex flex-col">
          <span className="text-[13px] font-semibold text-slate-900 dark:text-white">
            Link copied
          </span>
          <span className="text-[11px] text-slate-500 dark:text-white/40">
            Ready to share with employees
          </span>
        </div>
        <button
          onClick={onClose}
          className="ml-2 rounded-full p-1 text-slate-400 dark:text-white/30 hover:text-slate-600 dark:hover:text-white/60 transition-colors"
        >
          <X size={13} />
        </button>
      </div>
    </div>
  );
}

export default function QRShareCard({ code }) {
  const employeeLink = `https://healo.infiheal.com/?referralLink=${code}`;
  const [copied, setCopied] = useState(false);
  const [toastOpen, setToastOpen] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(employeeLink);
    } catch {}
    setCopied(true);
    setToastOpen(true);
    setTimeout(() => setCopied(false), 1800);
  };

  return (
    <>
      <CopyToast show={toastOpen} onClose={() => setToastOpen(false)} />

      <GlassCard
        accent="violet"
        className="flex flex-col text-slate-900 dark:text-white max-w-sm mx-auto"
      >
        <div className="relative flex items-center justify-center gap-2">
          <ScanLine size={14} className="text-slate-400 dark:text-white/40" />
          <h2 className="font-['Space_Grotesk',_sans-serif] text-[12px] font-semibold tracking-[0.2em] text-slate-400 dark:text-white/40 uppercase">
            Scan QR
          </h2>
        </div>

        {/* viewfinder / scanner frame */}
        <div className="relative mt-5 mx-auto w-56 h-56">
          <div
            className="absolute inset-0 rounded-2xl"
            style={{
              background:
                "radial-gradient(circle at 30% 20%, rgba(139,124,246,0.14), transparent 60%), radial-gradient(circle at 80% 80%, rgba(52,217,184,0.12), transparent 55%)",
            }}
          />
          <div
            className="absolute inset-0 rounded-2xl opacity-[0.35] dark:opacity-[0.25]"
            style={{
              backgroundImage:
                "radial-gradient(rgba(15,23,42,0.35) 1px, transparent 1px)",
              backgroundSize: "10px 10px",
            }}
          />
          <div className="absolute inset-0 rounded-2xl border border-black/[0.06] dark:border-white/[0.08] bg-white/40 dark:bg-white/[0.03] backdrop-blur-md" />

          {[
            "top-0 left-0 border-t-2 border-l-2 rounded-tl-xl",
            "top-0 right-0 border-t-2 border-r-2 rounded-tr-xl",
            "bottom-0 left-0 border-b-2 border-l-2 rounded-bl-xl",
            "bottom-0 right-0 border-b-2 border-r-2 rounded-br-xl",
          ].map((cls, i) => (
            <div
              key={i}
              className={`absolute w-7 h-7 ${cls}`}
              style={{ borderColor: i % 2 === 0 ? ACCENT_VIOLET : ACCENT_TEAL }}
            />
          ))}

          <div
            className="absolute left-3 right-3 h-px scan-line"
            style={{
              background: `linear-gradient(90deg, transparent, ${ACCENT_VIOLET}, ${ACCENT_TEAL}, transparent)`,
              boxShadow: `0 0 8px ${ACCENT_VIOLET}`,
            }}
          />

          <div className="absolute inset-5 rounded-xl bg-white shadow-[0_2px_12px_rgba(15,23,42,0.12)] p-3 flex items-center justify-center">
            <QRCodeSVG
              value={employeeLink}
              size={192}
              bgColor="#ffffff"
              fgColor="#0f172a"
              level="M"
              includeMargin={false}
            />
          </div>
        </div>

        {code && code =="AIIMS" ?  (<p className="relative mt-5 text-center text-[13px] leading-snug text-slate-500 dark:text-white/50">
          Students can scan this code to open your link directly.
        </p>):   (<p className="relative mt-5 text-center text-[13px] leading-snug text-slate-500 dark:text-white/50">
          Employees can scan this code to open your link directly.
        </p>)}

        <div className="relative mt-5 border-t border-black/[0.06] dark:border-white/[0.06]" />

        <button
          onClick={handleCopy}
          className="relative mt-5 w-full flex items-center justify-center gap-2 rounded-xl py-3 font-['Space_Grotesk',_sans-serif] font-semibold text-white text-[14px] transition-all duration-200 active:scale-[0.98] hover:shadow-[0_12px_30px_-10px_rgba(139,124,246,0.5)]"
          style={{
            background: `linear-gradient(90deg, ${ACCENT_VIOLET}, ${ACCENT_PINK})`,
          }}
        >
          {copied ? (
            <>
              <Check size={16} />
              Copied
            </>
          ) : (
            <>
              <Link2 size={16} />
              {code && code =="AIIMS"?(
               <> Copy Full Student Link </>
              ):(
               <>Copy Full Employee Link </> 
              )}
            </>
          )}
        </button>

       
        <style>{`
          .scan-line {
            animation: scan 2.4s ease-in-out infinite;
          }
          @keyframes scan {
            0% { top: 14%; opacity: 0; }
            10% { opacity: 1; }
            50% { top: 82%; opacity: 1; }
            60% { opacity: 0; }
            100% { top: 14%; opacity: 0; }
          }
        `}</style>
      </GlassCard>
    </>
  );
}
